import { ChevronDown, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { QuotesEvent, type QuotesFilter, useTrackQuotesEvent } from './quotesAnalytics'

/**
 * Selection count, sized to the chevron beside it and always in the DOM. A chip that
 * appeared on selection — like the label text that used to grow with it — would re-lay
 * the whole filter row out from under the pointer that just ticked a box.
 */
function FilterCount({ count }: { count: number }) {
  return (
    <Badge
      variant="secondary"
      aria-hidden={count === 0}
      className={cn(
        'bg-brand-800/15 size-4 min-w-4 px-0 text-[10px] tabular-nums',
        count === 0 && 'invisible'
      )}
    >
      {count}
    </Badge>
  )
}

interface FilterPopoverProps {
  /** Stable analytics key for this filter, independent of the label's wording. */
  filter: QuotesFilter
  /** Constant — never derived from the selection, so the trigger keeps its width. */
  label: string
  /** Number of options selected: drives the active styling and the count chip. */
  count: number
  /** Width class for the panel, sized to its longest option label. */
  panelClassName: string
  onClear: () => void
  children: React.ReactNode
}

/** One filter in the quotes-page filter bar: a fixed-width trigger over a panel of options. */
export function FilterPopover({
  filter,
  label,
  count,
  panelClassName,
  onClear,
  children,
}: FilterPopoverProps) {
  const track = useTrackQuotesEvent()

  return (
    <Popover
      onOpenChange={(open) =>
        track(open ? QuotesEvent.FILTER_OPENED : QuotesEvent.FILTER_CLOSED, {
          filter,
          selected_count: count,
        })
      }
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          // Font weight stays put: bold text is wider than normal, which shifted the row too.
          className={cn(
            'border-ink-100 gap-1.5 rounded-xl border font-normal',
            count > 0 ? 'border-brand-300 bg-brand-100' : 'bg-transparent'
          )}
        >
          {label}
          <FilterCount count={count} />
          <ChevronDown className="text-ink-300 size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        collisionPadding={8}
        className={cn('max-w-[calc(100vw-1rem)] p-3', panelClassName)}
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-bold">{label}</span>
          <PopoverClose asChild>
            <Button type="button" variant="iconOnly" size="icon-sm" aria-label="Close">
              <X className="text-ink-400 size-4" />
            </Button>
          </PopoverClose>
        </div>
        <div className="flex flex-col gap-1">{children}</div>
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={count === 0}
            onClick={() => {
              track(QuotesEvent.FILTER_CLEARED, { filter, selected_count: count })
              onClear()
            }}
          >
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface FilterOptionProps {
  /** Which filter this option belongs to — reported with every tick. */
  filter: QuotesFilter
  /** Stable value behind the label, e.g. the coverage type or the rating letter. */
  value: string
  label: string
  checked: boolean
  onToggle: () => void
}

/** A single tickable option inside a {@link FilterPopover}. */
export function FilterOption({ filter, value, label, checked, onToggle }: FilterOptionProps) {
  const track = useTrackQuotesEvent()

  return (
    <label className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5">
      <Checkbox
        checked={checked}
        onCheckedChange={() => {
          track(QuotesEvent.FILTER_TOGGLED, { filter, value, checked: !checked })
          onToggle()
        }}
      />
      <span className="text-sm font-medium">{label}</span>
    </label>
  )
}
